import React, { useState, useEffect } from 'react';
import { db, auth, app } from '../firebase';
import { getDatabase, ref, get } from 'firebase/database';
import '../styles/global.css';

const StudyProgress = () => {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        percent: 0,
        daysStudied: 0,
        streak: 0,
        approvedTopics: 0,
        totalTopics: 0,
        tasksToday: [],
        weeklyActivity: [10, 25, 45, 30, 60, 20, 0] // Mock weekly data
    });

    useEffect(() => {
        const fetchProgress = async () => {
            if (!auth.currentUser) return;

            try {
                const dbRTDB = getDatabase(app);
                const planesRef = ref(dbRTDB, `planes_estudio/${auth.currentUser.uid}`);
                const planesSnapshot = await get(planesRef);

                if (planesSnapshot.exists()) {
                    const planesData = planesSnapshot.val();
                    const planesArray = Object.values(planesData);
                    const planIds = Object.keys(planesData);
                    
                    // Tomamos el último plan creado
                    const plan = planesArray[planesArray.length - 1]; 
                    const planId = planIds[planIds.length - 1];

                    // Cargar progreso del plan
                    const progresoRef = ref(dbRTDB, `progreso_estudio/${auth.currentUser.uid}/${planId}`);
                    const progresoSnap = await get(progresoRef);
                    let temasCompletados = 0;
                    
                    if (progresoSnap.exists()) {
                        const progresoData = progresoSnap.val();
                        Object.values(progresoData).forEach(t => {
                            if (t.estado === 'COMPLETADO' || t.completado) {
                                temasCompletados++;
                            }
                        });
                    }

                    const start = new Date(plan.fechaCreacion || new Date());
                    let end = new Date(plan.fechaExamen);
                    if (isNaN(end.getTime())) {
                        end = new Date(start);
                        end.setDate(end.getDate() + 30); // 30 días si no hay examen
                    }
                    
                    const totalDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
                    
                    const totalTopics = plan.totalTemas || (plan.temasSeleccionados ? plan.temasSeleccionados.length : 0);
                    const percent = totalTopics > 0 ? Math.min(100, Math.round((temasCompletados / totalTopics) * 100)) : 0;

                    let streak = plan.rachaActual || 0;

                    setStats({
                        percent: percent,
                        daysStudied: Math.ceil((new Date() - start) / (1000 * 60 * 60 * 24)),
                        streak: streak,
                        approvedTopics: temasCompletados,
                        totalTopics: totalTopics,
                        tasksToday: plan.tutorMessage ? [plan.tutorMessage] : ['Repasar temas del día'],
                        weeklyActivity: [15, 30, 45, 60, 20, 40, 10] // Simulating activity
                    });
                    setProgress(plan);
                }
            } catch (e) {
                console.error("Error fetching study progress:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchProgress();
    }, []);

    if (loading) return <div className="loading">Cargando tu progreso...</div>;

    if (!progress) {
        return (
            <div className="study-progress" style={{ padding: '40px', textAlign: 'center' }}>
                <h1 style={{ color: 'var(--accent)' }}>Aún no tienes un Plan de Estudio</h1>
                <p style={{ opacity: 0.7, marginBottom: '20px' }}>Crea un plan en la app de Android para empezar a trackear tu progreso aquí.</p>
                <div style={{ fontSize: '5rem' }}>⏳</div>
            </div>
        );
    }

    return (
        <div className="study-progress" style={{ padding: '20px', color: 'var(--text-primary)', height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
            <div className="progress-header" style={{ marginBottom: '40px' }}>
                <h1 style={{ color: 'var(--accent)', margin: 0 }}>Panel de Rendimiento de Estudio</h1>
                <p style={{ opacity: 0.6 }}>Análisis detallado de tu preparación para el ascenso.</p>
            </div>

            <div className="analytics-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>

                {/* Main Progress Card */}
                <div className="card main-progress" style={{ background: 'var(--bg-secondary)', padding: '30px', borderRadius: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <h3>Progreso General del Plan</h3>
                    <div style={{ position: 'relative', width: '220px', height: '220px', margin: '30px 0' }}>
                        <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#222" strokeWidth="2.5" />
                            <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeDasharray={`${stats.percent}, 100`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
                        </svg>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', fontWeight: 'bold' }}>{stats.percent}%</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.5, letterSpacing: '2px' }}>META</div>
                        </div>
                    </div>
                    <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px' }}>
                            <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>Días Estudiados</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{stats.daysStudied}</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px' }}>
                            <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>Racha Actual</div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ffc107' }}>{stats.streak} 🔥</div>
                        </div>
                    </div>
                </div>

                {/* Activity Table & Topics */}
                <div style={{ display: 'grid', gap: '20px' }}>
                    <div className="card" style={{ background: 'var(--bg-secondary)', padding: '25px', borderRadius: '20px' }}>
                        <h3>Actividad Semanal (Carga Horaria)</h3>
                        <div style={{ height: '150px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '20px 0', gap: '10px' }}>
                            {stats.weeklyActivity.map((val, i) => (
                                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                    <div style={{
                                        width: '100%',
                                        height: `${val}%`,
                                        minHeight: '4px',
                                        background: 'linear-gradient(to top, var(--accent), #ff8c42)',
                                        borderRadius: '4px',
                                        transition: 'height 1s ease'
                                    }}></div>
                                    <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>{['L', 'M', 'M', 'J', 'V', 'S', 'D'][i]}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="card" style={{ background: 'var(--bg-secondary)', padding: '25px', borderRadius: '20px' }}>
                        <h3>Domino de Temas</h3>
                        <div style={{ marginTop: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span>Aprobados</span>
                                <span>{stats.approvedTopics} / {stats.totalTopics}</span>
                            </div>
                            <div style={{ height: '12px', background: '#222', borderRadius: '10px', overflow: 'hidden' }}>
                                <div style={{
                                    width: `${(stats.approvedTopics / (stats.totalTopics || 1)) * 100}%`,
                                    height: '100%',
                                    background: '#3498db',
                                    borderRadius: '10px'
                                }}></div>
                            </div>
                        </div>
                        <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <button className="btn-tiny" style={{ padding: '8px', borderRadius: '6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '0.8rem' }}>Ver Historial</button>
                            <button className="btn-tiny" style={{ padding: '8px', borderRadius: '6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '0.8rem' }}>Configurar Plan</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card" style={{ background: 'var(--bg-secondary)', padding: '30px', borderRadius: '20px', marginTop: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0 }}>Tareas Programadas para Hoy</h2>
                    <span style={{ padding: '5px 15px', background: 'var(--accent)', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold' }}>Hoy: {new Date().toLocaleDateString()}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    {stats.tasksToday.map((task, i) => (
                        <div key={i} style={{
                            padding: '20px',
                            background: 'rgba(255,255,255,0.03)',
                            borderRadius: '12px',
                            borderLeft: '4px solid var(--accent)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span>{task}</span>
                            <input type="checkbox" style={{ width: '20px', height: '20px', accentColor: 'var(--accent)' }} />
                        </div>
                    ))}
                </div>
                <button
                    onClick={() => {
                        const today = new Date().toISOString().split('T')[0];
                        setDoc(doc(db, `study_plans/${progress.id}/history`, today), { timestamp: new Date() })
                            .then(() => alert('¡Progreso diario guardado! 🔥'))
                            .catch(e => alert(e.message));
                    }}
                    style={{
                        marginTop: '30px',
                        width: '100%',
                        padding: '18px',
                        background: 'var(--accent)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50px',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        cursor: 'pointer',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
                    }}
                >
                    🚀 COMPLETAR SESIÓN DE ESTUDIO
                </button>
            </div>
        </div>
    );
};

export default StudyProgress;
